import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Languages, Mail, Phone, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { get } from "@/lib/api";

export function useNotice(url, lang) {
  return useQuery({ queryKey: ["notice", url, lang], queryFn: () => get(url, { lang }), staleTime: 60_000 });
}

export function LangToggle({ notice, lang, setLang }) {
  if (!notice) return null;
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-line bg-white p-0.5 text-xs" role="group" aria-label="Notice language" data-testid="notice-lang-toggle">
      <Languages size={13} className="ml-2 text-ink-muted" aria-hidden="true" />
      {notice.languages.map((l) => (
        <button key={l.code} type="button" onClick={() => setLang(l.code)} aria-pressed={lang === l.code} data-testid={`notice-lang-${l.code}`}
          className={`rounded-full px-2.5 py-1 transition-colors duration-150 ${lang === l.code ? "bg-brand text-white" : "text-ink-secondary hover:bg-subtle"}`}>{l.label}</button>
      ))}
    </div>
  );
}

export function NoticeBody({ notice }) {
  return (
    <div className="space-y-4 text-sm leading-relaxed text-ink-secondary" data-testid="privacy-notice-body" lang={notice.lang}>
      {notice.machine_translated && <p className="rounded-[5px] bg-subtle px-3 py-2 text-xs" data-testid="notice-machine-translated">This translation was generated automatically. If anything is unclear, the English version applies.</p>}
      <p>{notice.intro}</p>
      {notice.sections.map((s) => (
        <section key={s.id} data-testid={`notice-section-${s.id}`}>
          <h4 className="mb-1 font-semibold text-ink">{s.title}</h4>
          {s.items ? <ul className="list-disc space-y-0.5 pl-5">{s.items.map((i) => <li key={i}>{i}</li>)}</ul> : <p>{s.body}</p>}
        </section>
      ))}
      <section className="rounded-[6px] border border-line bg-subtle p-3" data-testid="notice-dpo">
        <h4 className="mb-1 flex items-center gap-1.5 font-semibold text-ink"><ShieldCheck size={14} aria-hidden="true" /> {notice.dpo.title}</h4>
        <p data-testid="notice-dpo-name">{notice.dpo.name}</p>
        {notice.dpo.email && <p className="flex items-center gap-1.5"><Mail size={13} aria-hidden="true" /><a className="text-brand underline" href={`mailto:${notice.dpo.email}`} data-testid="notice-dpo-email">{notice.dpo.email}</a></p>}
        {notice.dpo.phone && <p className="flex items-center gap-1.5" data-testid="notice-dpo-phone"><Phone size={13} aria-hidden="true" />{notice.dpo.phone}</p>}
      </section>
      <p className="text-xs text-ink-muted" data-testid="notice-version">Notice version {notice.version}</p>
    </div>
  );
}

export function NoticePanel({ url, lang, setLang, className = "" }) {
  const { data } = useNotice(url, lang);
  if (!data) return <div className="h-40 animate-pulse rounded-[6px] bg-subtle" />;
  return (
    <div className={`space-y-3 rounded-[6px] border border-line p-4 ${className}`} data-testid="privacy-notice-panel">
      <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="text-sm font-semibold text-ink">{data.title}</h3><LangToggle notice={data} lang={lang} setLang={setLang} /></div>
      <div className="max-h-72 overflow-y-auto pr-1"><NoticeBody notice={data} /></div>
    </div>
  );
}

export function NoticeDialogLink({ url = "/public/privacy/notice", label = "Read the full privacy notice", testId = "notice-dialog-link" }) {
  const [lang, setLang] = useState("en");
  const { data } = useNotice(url, lang);
  return (
    <Dialog>
      <DialogTrigger asChild><button type="button" className="text-brand underline-offset-2 hover:underline" data-testid={testId}>{label}</button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl" data-testid="notice-dialog">
        <DialogHeader><DialogTitle>{data?.title || "Privacy notice"}</DialogTitle></DialogHeader>
        {data && <><LangToggle notice={data} lang={lang} setLang={setLang} /><NoticeBody notice={data} /></>}
      </DialogContent>
    </Dialog>
  );
}
